import { type Task, TaskType } from "@/api.types.v2";
import { getOrderSenderInfo } from "@/lib/safe-access";

export const getTaskTypeDisplay = (type: Task["type"]) => {
  const typeMap: Record<string, string> = {
    [TaskType.CUSTOMER_PICKUP]: "Pickup",
    [TaskType.CUSTOMER_DELIVERY]: "Delivery",
    [TaskType.CUSTOMER_RETURN]: "Return",
    [TaskType.CARRIER_DROPOFF]: "Carrier Dropoff",
    [TaskType.CARRIER_PICKUP]: "Carrier Pickup",
    [TaskType.DEPOT_RECEIVE]: "Dispatch Center Receive",
    [TaskType.DEPOT_SORT]: "Dispatch Center Sort",
    [TaskType.DEPOT_DISPATCH]: "Dispatch Center Dispatch",
    [TaskType.DEPOT_TRANSFER]: "Dispatch Center Transfer",
  };
  return typeMap[type] || type.replace(/_/g, " ").toLowerCase();
};

// Get the relevant party info based on task type
export const getTaskPartyInfo = (task: Task): string => {
  const shipmentAddresses = task.shipment?.addresses;

  switch (task.type) {
    case TaskType.CUSTOMER_PICKUP:
      // For customer pickup, use denormalized contactName or shipment addresses
      if (task.contactName) {
        return `Pick up from ${task.contactName}`;
      }
      if (shipmentAddresses) {
        const fromAddress = shipmentAddresses.find((a) => a.type === "FROM");
        if (fromAddress?.contactName) {
          return `Pick up from ${fromAddress.contactName}`;
        }
      }
      return "⚠️ Missing sender information";

    case TaskType.CUSTOMER_DELIVERY:
      // For customer delivery, use denormalized contactName or shipment addresses
      if (task.contactName) {
        return `Deliver to ${task.contactName}`;
      }
      if (shipmentAddresses) {
        const toAddress = shipmentAddresses.find((a) => a.type === "TO");
        if (toAddress?.contactName) {
          return `Deliver to ${toAddress.contactName}`;
        }
      }
      return "⚠️ Missing recipient information";

    case TaskType.CUSTOMER_RETURN:
      // For customer return, try RETURN address first, then fallback to FROM address
      if (shipmentAddresses) {
        const returnAddress = shipmentAddresses.find(
          (a) => a.type === "RETURN"
        );
        if (returnAddress?.contactName) {
          return `Return to ${returnAddress.contactName}`;
        }
        // Fallback to FROM address if no RETURN address exists
        const fromAddress = shipmentAddresses.find((a) => a.type === "FROM");
        if (fromAddress?.contactName) {
          return `Return to ${fromAddress.contactName}`;
        }
      }
      return "⚠️ Missing return address";

    case TaskType.CARRIER_DROPOFF:
      // For carrier dropoff, show the dispatch center name if available, otherwise the address
      if (task.depot?.name) {
        return `Drop off at ${task.depot.name}`;
      }
      if (task.address) {
        // Fallback to address if dispatch center name not available
        return `Drop off at ${task.address.line1}`;
      }
      if (task.shipment?.carrier?.name) {
        return `Drop off at ${task.shipment.carrier.name} location`;
      }
      return "⚠️ Missing dropoff location";

    default:
      // For other task types, try to get relevant info
      if (shipmentAddresses && shipmentAddresses.length > 0) {
        const address = shipmentAddresses[0];
        if (address?.contactName) {
          return address.contactName;
        }
      }
  }

  // Fallback to customer info from order
  const order = task.order ?? task.shipment?.order;
  if (order?.placerCustomer) {
    // Get customer name or company
    const customer = order.placerCustomer;
    const rawCompanyName =
      customer.businessAccount?.companyName ?? customer.metadata?.companyName;
    if (typeof rawCompanyName === "string" && rawCompanyName) {
      return rawCompanyName;
    }
    // Try to get customer name directly
    if (customer.firstName || customer.lastName) {
      return `${customer.firstName ?? ""} ${customer.lastName ?? ""}`.trim();
    }
  }

  // Last resort fallback to order info
  if (order) {
    const senderInfo = getOrderSenderInfo(order);
    if (senderInfo?.name && senderInfo.name !== "Unknown") {
      return senderInfo.name;
    }
  }

  return "Customer";
};

// Keep for backwards compatibility but simplified
export const getTaskSenderInfo = (): string => {
  return "";
};

// Keep for backwards compatibility - not used in new display
export const getTaskRecipientInfo = (): string => {
  return "";
};
